import { z } from 'zod'
import { linkedEventSummarySchema, participantStatusValues, planTypeValues } from './plan'

/**
 * Display snapshot carried by a `plan_vote` chat message. Never holds vote
 * state — the card reads live state from the plan itself, so the message can
 * not go stale.
 */
export const planVoteMetadataSchema = z.object({
  planId: z.string().uuid(),
  eventId: z.string().uuid(),
  title: z.string().min(1).max(200),
  coverImageUrl: z.string().nullable(),
  startTime: z.string().nullable(),
  endTime: z.string().nullable(),
  locationName: z.string().nullable(),
})

export type PlanVoteMetadata = z.infer<typeof planVoteMetadataSchema>

/**
 * Slim state for rendering a vote card — everything the card needs and
 * nothing the full plan detail carries (which costs ~10 queries per fetch).
 */
export const planVoteStateSchema = z.object({
  planId: z.string().uuid(),
  planType: z.enum(planTypeValues),
  creatorId: z.string().uuid(),
  expiredAt: z.string().nullable(),
  /** The plan's current name — a card's own snapshot keeps the name it was sent with. */
  name: z.string(),
  /** When the plan is set for, so a card can say so and stop taking answers once it is past. */
  date: z.string().nullable(),
  timeStart: z.string().nullable(),
  timeEnd: z.string().nullable(),
  timezone: z.string().nullable(),
  /** The plan takes the whole day, so a card shows its date without a time. */
  allDay: z.boolean(),
  myStatus: z.enum(participantStatusValues).nullable(),
  linkedEvent: linkedEventSummarySchema.nullable(),
  participants: z.array(
    z.object({
      userId: z.string().uuid(),
      displayName: z.string().nullable(),
      avatarUrl: z.string().nullable(),
      status: z.enum(participantStatusValues),
    }),
  ),
  /** Users holding a pending invite — the picker marks them so nobody is asked twice. */
  invitedUserIds: z.array(z.string().uuid()),
  /** Group conversations that already hold an invite card for this plan. */
  invitedConversationIds: z.array(z.string().uuid()),
  /** Joining needs the creator's approval unless the viewer holds a personal invite. */
  requireApproval: z.boolean().optional(),
  /** The viewer holds a pending personal invite, so they may join directly. */
  myInvitePending: z.boolean().optional(),
  /** The viewer has asked to join and the creator has not decided yet. */
  myJoinRequestPending: z.boolean().optional(),
})

export type PlanVoteState = z.infer<typeof planVoteStateSchema>
